import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';

import { cn } from '@/lib/utils';

function formatElapsed(startedAt: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Live elapsed since `startedAt`, formatted H:MM:SS (or MM:SS under an hour).
 *  Seeded via a lazy initializer, then ticked each second by the interval — the
 *  impure clock read never happens during the render body. */
function useElapsed(startedAt: string): string {
  const [label, setLabel] = useState(() => formatElapsed(startedAt));
  useEffect(() => {
    const id = setInterval(() => setLabel(formatElapsed(startedAt)), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return label;
}

/** Live "⏱ H:MM:SS" ticker since `startedAt`. Reused by the work-item drawer and the stop-note dialog. */
export function TimerElapsed({ startedAt, className }: { startedAt: string; className?: string }) {
  const elapsed = useElapsed(startedAt);
  return (
    <span className={cn('flex items-center gap-1 font-mono tabular-nums', className)}>
      <Timer className="size-3.5" />
      {elapsed}
    </span>
  );
}
